
import { useEffect, useMemo, useRef } from 'react';
import { useAiAutoAnswerSurveyPauseMutation, useAiAutoAnswerSurveyResumeMutation, useAiAutoAnswerSurveyAbortMutation, useGetAIAutoAnswerSurveyQuery } from '../../api/ai';
import { CollapsibleContainer } from './partials/CollapsibleContainer';
import { AgentContainer } from './partials/AgentContainer';
import { AgentResponseContainer } from './partials/AgentResponseContainer';
import { useAuthProtectedStreamUrl } from './hooks';
import { AgentThinking, StreamData, StreamDataType } from './types';
import { Button } from 'reactstrap';
import IconButton from '../../molecules/button/IconButton';
import { JobStatus } from '@g17eco/types/background-jobs';
import { useSiteAlert } from '../../hooks/useSiteAlert';
import { ButtonGradient } from '../../atoms/button';

interface AIAutoAnswerStreamingProps {
  initiativeId: string;
  surveyId: string;
  toggleStreaming: () => void;
}

export const AIAutoAnswerStreaming = (props: AIAutoAnswerStreamingProps) => {
  const { initiativeId, surveyId, toggleStreaming } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const { addSiteError, addSiteAlert } = useSiteAlert();
  const [autoAnswerSurveyPause] = useAiAutoAnswerSurveyPauseMutation();
  const [autoAnswerSurveyResume] = useAiAutoAnswerSurveyResumeMutation();

  const { data: jobData } = useGetAIAutoAnswerSurveyQuery(
    { initiativeId, surveyId }
  );

  const job = jobData?.job;
  const jobId = job?._id;
  const isPaused = job?.status === JobStatus.Paused || job?.status === JobStatus.Pending;
  const isProcessing = job?.status === JobStatus.Processing;

  const [autoAnswerSurveyAbort] = useAiAutoAnswerSurveyAbortMutation();
  const { data = [], clear: clearStreamingLogs } = useAuthProtectedStreamUrl(`initiatives/${initiativeId}/streaming/survey/${surveyId}/auto-answer/${jobId}`, jobId);
  // const { data = [] } = useAuthProtectedStream( useStreamSurveyJobQuery, { initiativeId, surveyId, jobId }, initiativeId );

  const handlePause = () => {
    autoAnswerSurveyPause({ initiativeId, surveyId })
      .unwrap()
      .then(() => addSiteAlert({
        content: 'Auto answering scheduled to be paused after the current metric is processed. This may take a few moments to complete.',
      }))
      .catch((e) => {
        console.error('Error pausing auto answering', e);
        addSiteError(e)
    });
  };

  const handleResume = () => {
    clearStreamingLogs();
    autoAnswerSurveyResume({ initiativeId, surveyId })
      .unwrap()
      .then(() => addSiteAlert({
        content: 'Auto answering scheduled to be resumed. This may take a few moments to begin.',
      }))
      .catch((e) => addSiteError(e));
  };

  const handleAbort = () => {
    autoAnswerSurveyAbort({ initiativeId, surveyId })
      .unwrap()
      .then(() => addSiteAlert({
        content: 'Auto answering is being cancelled. This may take a few moments to complete.',
      }))
      .catch((e) => addSiteError(e));
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const containerTop = container.scrollHeight - container.scrollTop - container.clientHeight;
      const isScrolledToBottom = containerTop <= 2 * container.clientHeight;
      if (isScrolledToBottom) {
        setTimeout(() => container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        }), 100);
      }
    }
  }, [data]);

  const elements = useMemo(() => {
    const elements = [];
    let thinkingGroup: StreamData[] = [];

    data.forEach((msg, i) => {
      if (msg.type === StreamDataType.AgentThinking) {
        thinkingGroup.push(msg);
      } else {
        if (thinkingGroup.length > 0) {
          elements.push(
            <CollapsibleContainer key={`thinking-${i}`} dataArray={thinkingGroup as AgentThinking[]} isThinking={false} togglerId={`toggler-${i}`} endTimestamp={msg.timestamp} />
          );
          thinkingGroup = [];
        }
        if (msg.type === StreamDataType.AgentMessage) {
          elements.push(<AgentContainer key={i} data={msg} />);
        } else if (msg.type === StreamDataType.AgentResponse) {
          elements.push(<AgentResponseContainer key={i} data={msg} />);
        }
      }
    });

    // Add any remaining thinking messages
    if (thinkingGroup.length > 0) {
      elements.push(
        <CollapsibleContainer key={'thinking-end'} dataArray={thinkingGroup as AgentThinking[]} isThinking={true} togglerId={'toggler-end'} />
      );
    }

    return elements;
  }, [data]);

  if (!jobId) {
    return null;
  }

  return (
    // Add a button to close streaming and another to toggle full-screen mode
    <div className={'ai-streaming-container'}>
      <div className='ai-streaming-blur' />
      <div className={'w-100 d-flex flex-column p-4 overflow-y-auto background-ThemeBgExtralight ai-streaming shadow-sm rounded-lg border'}>
        <div className='d-flex justify-content-between border-bottom mt-0 mb-4 pb-2'>
          <div className=' strong'>Auto-Answering Progress</div>
          <div className='d-flex gap-2'>
            {isPaused ? (
              <>
                <ButtonGradient onClick={handleResume}>
                  <i className='fal fa-play mr-2' />
                  Continue Auto Answering...
                </ButtonGradient>
                <Button color='danger' outline onClick={handleAbort}>
                  <i className='fal fa-times text-danger me-2' />
                  Abort
                </Button>
              </>
            ) : isProcessing ? (
                <Button color='secondary' onClick={handlePause}>
                  <i className='fal fa-pause text-ThemeIconSecondary me-2' />
                  Pause
                </Button>
            ) : null}
            <IconButton icon='fas fa-close' color='primary' onClick={toggleStreaming} />
          </div>
        </div>
        <div ref={containerRef} className='ai-streaming-content flex-grow-1 space-y-4' >
          {elements.map((element, i) => <div key={i}>{element}</div>)}
        </div>
      </div>
    </div>
  );
}
